import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import type { PurchaseOrder } from "serviceStores/mock/purchase-orders.mock";
import type { PurchaseOrderLineItem } from "serviceStores/mock/purchase-order-line-items.mock";
import type { GLAccount } from "serviceStores/mock/gl-accounts.mock";
import type { ApprovalQueueTask } from "serviceStores/mock/approval-queue.mock";

/**
 * Line item with GL verification
 */
interface ApprovalLineItem extends PurchaseOrderLineItem {
  glVerified: boolean;
  suggestedGLAccountId?: number;
  glChangeReason?: string;
}

/**
 * Document info for review
 */
interface DocumentInfo {
  id: string;
  type: "quote" | "packing_slip" | "invoice" | "freight_bill" | "other";
  name: string;
  url: string;
  uploadedAt: string;
  reviewed: boolean;
}

/**
 * Form data for PO approval
 */
interface POApprovalFormData {
  // Selected task/PO
  taskId: number | null;
  task: ApprovalQueueTask | null;
  purchaseOrder: PurchaseOrder | null;

  // Step 1: Document Review
  documents: DocumentInfo[];
  allDocumentsReviewed: boolean;

  // Step 2: GL Verification
  lineItems: ApprovalLineItem[];
  glVerificationComplete: boolean;

  // Step 3: Decision
  decision: "approved" | "rejected" | null;
  approvalNotes: string;
  rejectionReason: string;
  requiresFollowUp: boolean;
  followUpNotes: string;
}

/**
 * Initial form data
 */
const initialFormData: POApprovalFormData = {
  taskId: null,
  task: null,
  purchaseOrder: null,
  documents: [],
  allDocumentsReviewed: false,
  lineItems: [],
  glVerificationComplete: false,
  decision: null,
  approvalNotes: "",
  rejectionReason: "",
  requiresFollowUp: false,
  followUpNotes: "",
};

/**
 * PO Approval Wizard Store
 *
 * Manages state for the Purchase Order approval wizard.
 * Used by approvers to review documents, verify GL codes, and approve/reject POs.
 */
class POApprovalWizardStore {
  rootStore: RootStore;

  // Step navigation
  activeStep = 0;
  readonly steps = ["Review Documents", "Verify GL Codes", "Approve/Reject"];

  // UI State
  loading = false;
  error: string | null = null;
  success: string | null = null;

  // Form Data
  formData: POApprovalFormData = { ...initialFormData };

  // Reference Data
  glAccounts: GLAccount[] = [];
  pendingTasks: ApprovalQueueTask[] = [];

  // Document viewer state
  activeDocumentIndex = 0;
  documentViewerOpen = false;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // ==================== Step Navigation ====================

  setActiveStep = (step: number) => {
    this.activeStep = step;
  };

  nextStep = () => {
    if (this.validateCurrentStep()) {
      this.activeStep++;
    }
  };

  prevStep = () => {
    if (this.activeStep > 0) {
      this.activeStep--;
    }
  };

  canProceed = (): boolean => {
    return this.validateCurrentStep(false);
  };

  // ==================== Task Selection ====================

  selectTask = async (taskId: number) => {
    const task = this.pendingTasks.find((t) => t.taskId === taskId);
    if (!task) return;

    this.loading = true;
    this.formData.taskId = taskId;
    this.formData.task = task;

    try {
      // Load the purchase order
      const purchaseOrdersStore = (this.rootStore as any).purchaseOrdersStore;
      if (purchaseOrdersStore) {
        const pos = purchaseOrdersStore.entities.filter(
          (po: PurchaseOrder) => po.purchaseOrderId === task.entityId
        );
        if (pos.length > 0) {
          runInAction(() => {
            this.formData.purchaseOrder = pos[0];
          });
        }
      }

      // Load line items
      await this.loadLineItemsForPO(task.entityId);

      // Generate mock documents
      runInAction(() => {
        this.generateMockDocuments();
        this.loading = false;
      });
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || "Failed to load PO details";
        this.loading = false;
      });
    }
  };

  loadLineItemsForPO = async (poId: number) => {
    const lineItemsStore = (this.rootStore as any).purchaseOrderLineItemsStore;
    if (lineItemsStore) {
      await lineItemsStore.find({
        query: { purchaseOrderId: poId },
      });

      runInAction(() => {
        const items = lineItemsStore.entities.filter(
          (item: PurchaseOrderLineItem) => item.purchaseOrderId === poId
        );

        this.formData.lineItems = items.map((item: PurchaseOrderLineItem) => ({
          ...item,
          glVerified: false,
          suggestedGLAccountId: undefined,
          glChangeReason: undefined,
        }));
      });
    }
  };

  generateMockDocuments = () => {
    const po = this.formData.purchaseOrder;
    if (!po) return;

    const docs: DocumentInfo[] = [];

    if (po.hasQuoteDocument) {
      docs.push({
        id: `doc-quote-${po.purchaseOrderId}`,
        type: "quote",
        name: `Quote - ${po.vendorName}.pdf`,
        url: "#",
        uploadedAt: po.orderDate,
        reviewed: false,
      });
    }

    if (po.hasPackingSlip) {
      docs.push({
        id: `doc-packing-${po.purchaseOrderId}`,
        type: "packing_slip",
        name: `Packing Slip - ${po.poNumber}.pdf`,
        url: "#",
        uploadedAt: po.lastReceivedDate || po.orderDate,
        reviewed: false,
      });
    }

    if (po.hasInvoice) {
      docs.push({
        id: `doc-invoice-${po.purchaseOrderId}`,
        type: "invoice",
        name: `Invoice - ${po.invoiceNumber || po.poNumber}.pdf`,
        url: "#",
        uploadedAt: po.invoiceDate || po.orderDate,
        reviewed: false,
      });
    }

    if (po.hasFreightBill && po.freightType !== "none") {
      docs.push({
        id: `doc-freight-${po.purchaseOrderId}`,
        type: "freight_bill",
        name: `Freight Bill - ${po.freightInvoiceNumber || "N/A"}.pdf`,
        url: "#",
        uploadedAt: po.orderDate,
        reviewed: false,
      });
    }

    this.formData.documents = docs;
  };

  clearSelectedTask = () => {
    this.formData = { ...initialFormData };
    this.activeStep = 0;
  };

  // ==================== Document Review ====================

  markDocumentReviewed = (docId: string, reviewed: boolean) => {
    const doc = this.formData.documents.find((d) => d.id === docId);
    if (doc) {
      doc.reviewed = reviewed;
    }
    this.checkAllDocumentsReviewed();
  };

  checkAllDocumentsReviewed = () => {
    this.formData.allDocumentsReviewed =
      this.formData.documents.length > 0 &&
      this.formData.documents.every((d) => d.reviewed);
  };

  setActiveDocument = (index: number) => {
    this.activeDocumentIndex = index;
  };

  openDocumentViewer = (index?: number) => {
    if (index !== undefined) {
      this.activeDocumentIndex = index;
    }
    this.documentViewerOpen = true;
  };

  closeDocumentViewer = () => {
    this.documentViewerOpen = false;
  };

  // ==================== GL Verification ====================

  verifyLineItemGL = (lineItemId: number, verified: boolean) => {
    const item = this.formData.lineItems.find((i) => i.lineItemId === lineItemId);
    if (item) {
      item.glVerified = verified;
    }
    this.checkGLVerificationComplete();
  };

  updateLineItemGL = (
    lineItemId: number,
    newGLAccountId: number,
    reason: string
  ) => {
    const item = this.formData.lineItems.find((i) => i.lineItemId === lineItemId);
    if (item) {
      item.suggestedGLAccountId = newGLAccountId;
      item.glChangeReason = reason;
      item.glVerified = true;
    }
    this.checkGLVerificationComplete();
  };

  verifyAllGL = () => {
    this.formData.lineItems.forEach((item) => {
      item.glVerified = true;
    });
    this.formData.glVerificationComplete = true;
  };

  checkGLVerificationComplete = () => {
    this.formData.glVerificationComplete = this.formData.lineItems.every(
      (item) => item.glVerified
    );
  };

  // ==================== Decision ====================

  setDecision = (decision: "approved" | "rejected") => {
    this.formData.decision = decision;
  };

  updateApprovalNotes = (notes: string) => {
    this.formData.approvalNotes = notes;
  };

  updateRejectionReason = (reason: string) => {
    this.formData.rejectionReason = reason;
  };

  toggleFollowUp = (required: boolean) => {
    this.formData.requiresFollowUp = required;
    if (!required) {
      this.formData.followUpNotes = "";
    }
  };

  updateFollowUpNotes = (notes: string) => {
    this.formData.followUpNotes = notes;
  };

  // ==================== Computed Values ====================

  get documentsReviewedCount(): number {
    return this.formData.documents.filter((d) => d.reviewed).length;
  }

  get glVerifiedCount(): number {
    return this.formData.lineItems.filter((i) => i.glVerified).length;
  }

  get hasGLChanges(): boolean {
    return this.formData.lineItems.some((i) => i.suggestedGLAccountId);
  }

  get totalAmount(): number {
    return this.formData.purchaseOrder?.totalAmount || 0;
  }

  // ==================== Validation ====================

  validateCurrentStep = (showError = true): boolean => {
    this.error = null;

    switch (this.activeStep) {
      case 0: // Document Review
        if (!this.formData.purchaseOrder) {
          if (showError) this.error = "Please select a Purchase Order to review";
          return false;
        }
        if (!this.formData.allDocumentsReviewed) {
          if (showError)
            this.error = "Please review all documents before proceeding";
          return false;
        }
        return true;

      case 1: // GL Verification
        if (!this.formData.glVerificationComplete) {
          if (showError)
            this.error = "Please verify GL codes for all line items";
          return false;
        }
        return true;

      case 2: // Decision
        if (!this.formData.decision) {
          if (showError) this.error = "Please select Approve or Reject";
          return false;
        }
        if (
          this.formData.decision === "rejected" &&
          !this.formData.rejectionReason
        ) {
          if (showError)
            this.error = "Please provide a reason for rejection";
          return false;
        }
        if (
          this.formData.requiresFollowUp &&
          !this.formData.followUpNotes
        ) {
          if (showError) this.error = "Please provide follow-up notes";
          return false;
        }
        return true;

      default:
        return true;
    }
  };

  // ==================== Data Loading ====================

  loadInitialData = async () => {
    this.loading = true;
    this.error = null;

    try {
      // Load GL accounts
      if ((this.rootStore as any).glAccountsStore) {
        await (this.rootStore as any).glAccountsStore.find({
          query: { isActive: true },
        });
      }

      // Load pending approval tasks
      if ((this.rootStore as any).approvalQueueStore) {
        await (this.rootStore as any).approvalQueueStore.find({
          query: {
            queueType: "approval",
            status: "pending",
          },
        });
      }

      // Load POs for context
      if ((this.rootStore as any).purchaseOrdersStore) {
        await (this.rootStore as any).purchaseOrdersStore.find({
          query: { status: "pending_approval" },
        });
      }

      runInAction(() => {
        this.glAccounts =
          (this.rootStore as any).glAccountsStore?.entities || [];
        const allTasks =
          (this.rootStore as any).approvalQueueStore?.entities || [];
        this.pendingTasks = allTasks.filter(
          (t: ApprovalQueueTask) =>
            t.queueType === "approval" && t.status === "pending"
        );
        this.loading = false;
      });
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || "Failed to load initial data";
        this.loading = false;
      });
    }
  };

  // ==================== Submission ====================

  submitDecision = async () => {
    if (!this.validateCurrentStep()) {
      return;
    }

    this.loading = true;
    this.error = null;

    try {
      const po = this.formData.purchaseOrder!;
      const task = this.formData.task!;
      const isApproved = this.formData.decision === "approved";

      // Update line items with GL changes if any
      if (this.hasGLChanges) {
        const lineItemsStore = (this.rootStore as any).purchaseOrderLineItemsStore;
        if (lineItemsStore) {
          for (const item of this.formData.lineItems) {
            if (item.suggestedGLAccountId) {
              await lineItemsStore.patch(item.lineItemId, {
                glAccountId: item.suggestedGLAccountId,
                glChangeReason: item.glChangeReason,
              });
            }
          }
        }
      }

      // Update PO status
      const purchaseOrdersStore = (this.rootStore as any).purchaseOrdersStore;
      if (purchaseOrdersStore) {
        await purchaseOrdersStore.patch(po.purchaseOrderId, {
          status: isApproved ? "approved" : "rejected",
          approvedBy: isApproved
            ? (this.rootStore as any).authStore?.user?.id || 1
            : undefined,
          approvedByName: isApproved
            ? (this.rootStore as any).authStore?.user?.name || "Current User"
            : undefined,
          approvedDate: isApproved
            ? new Date().toISOString().split("T")[0]
            : undefined,
          rejectionReason: !isApproved
            ? this.formData.rejectionReason
            : undefined,
        });
      }

      // Update approval queue task
      const approvalQueueStore = (this.rootStore as any).approvalQueueStore;
      if (approvalQueueStore) {
        await approvalQueueStore.patch(task.taskId, {
          status: "completed",
          completedDate: new Date().toISOString().split("T")[0],
          completedBy: (this.rootStore as any).authStore?.user?.id || 1,
          completedByName:
            (this.rootStore as any).authStore?.user?.name || "Current User",
          resolution: isApproved ? "approved" : "rejected",
          notes: isApproved
            ? this.formData.approvalNotes
            : this.formData.rejectionReason,
        });

        // If approved, create payment queue task
        if (isApproved) {
          await approvalQueueStore.create({
            queueType: "payment",
            entityType: "purchase_order",
            entityId: po.purchaseOrderId,
            entityNumber: po.poNumber,
            plantId: po.plantId,
            plantCode: po.plantCode,
            title: `Pay PO ${po.poNumber}`,
            description: `${po.vendorName} - ${po.totalAmount.toLocaleString(
              "en-US",
              { style: "currency", currency: "USD" }
            )}`,
            priority: po.priority === "urgent" ? "urgent" : "normal",
            status: "pending",
            createdDate: new Date().toISOString().split("T")[0],
            dueDate: po.invoiceDueDate ||
              new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
                .toISOString()
                .split("T")[0],
            vendorName: po.vendorName,
            totalAmount: po.totalAmount,
            invoiceNumber: po.invoiceNumber,
          });
        }
      }

      runInAction(() => {
        this.success = isApproved
          ? `PO ${po.poNumber} has been approved and sent to payment queue!`
          : `PO ${po.poNumber} has been rejected.`;
        this.loading = false;

        // Navigate to approval queue after delay
        setTimeout(() => {
          this.reset();
          this.rootStore.routerStore.goTo("ApprovalQueue");
        }, 2000);
      });
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || "Failed to submit decision";
        this.loading = false;
      });
    }
  };

  // ==================== Reset ====================

  reset = () => {
    this.activeStep = 0;
    this.loading = false;
    this.error = null;
    this.success = null;
    this.formData = { ...initialFormData, documents: [], lineItems: [] };
    this.activeDocumentIndex = 0;
    this.documentViewerOpen = false;
  };
}

export default POApprovalWizardStore;
